import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, Inject, InjectionToken, forwardRef, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import MetaFileLink from 'src/app/models/metaFileLink.model';
import { FileTypeUtils } from "src/app/shared/utils";
import { UploaderBlockComponent } from '../uploader-block/uploader-block.component';
import { DocumentFileIconComponent } from '../document-file-icon/document-file-icon.component';
import { CovercySharedComponentsModule } from '../../CovercySharedComponents.module';

export const WINDOW = new InjectionToken<Window>('Global window object', {
  factory: () => window
});

@Component({
  selector: 'terra-file-attachments',
  templateUrl: './file-attachments.component.html',
  styleUrls: ['./file-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileAttachmentsComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    UploaderBlockComponent,
    DocumentFileIconComponent,
    CovercySharedComponentsModule
  ]
})
export class FileAttachmentsComponent implements AfterViewInit, ControlValueAccessor {
  @Input() label: string = 'Attachments:';
  @Input() acceptedFileTypes: any[] = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.mp3',
    '.mp4',
    '.avi',
    '.ppt',
    '.pptx',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.csv'
  ];
  @Input() maxTotalSize: number = 10 * 1024 * 1024; // 10MB in bytes
  @Input() multiple: boolean = true;
  @Input() showUploader: boolean = true;
  @Input() touchedCallback: () => void = () => { };
  /**
   * MANDATORY: This event should be handled to disable form submission when file size is exceeded.
   * Emits 'true' when total file size exceeds maxTotalSize, 'false' otherwise.
   * Usage example: (sizeExceeded)="onAttachmentSizeExceeded($event)"
   */
  @Output() sizeExceeded = new EventEmitter<boolean>();
  /**
   * MANDATORY: This event should be handled to disable form submission when any file is failed or not finished uploading.
   * Emits 'true' when at least one file is failed (progress === -1) or not finished uploading (progress !== 100), 'false' otherwise.
   * Usage example: (uploadStatusInvalid)="onAttachmentUploadStatusInvalid($event)"
   */
  @Output() uploadStatusInvalid = new EventEmitter<boolean>();

  @ViewChild('uploader', { static: true }) uploader!: UploaderBlockComponent;

  private _files: MetaFileLink[] = [];

  get files(): MetaFileLink[] {
    return this._files;
  }
  set files(files: MetaFileLink[]) {
    this._files = files || [];
    this.calculateTotalSize();
    this.checkUploadStatusInvalid();
  }

  mediaTypeConverter = FileTypeUtils.mediaTypeConverter
  totalAttachmentsSize = 0;
  attachmentsSizeExceeded = false;
  disabled = false;
  constructor(
    @Inject(WINDOW) private window: Window,
  ) { }
  ngAfterViewInit(): void {
    this.uploader.valueChanged.subscribe(files => {
      this.files = files || [];
      this.onChange(this.files);
      this.onTouched();
    });

    // Check if sizeExceeded has observers and warn if not
    setTimeout(() => {
      if (!this.sizeExceeded.observed) {
        console.warn('WARNING: terra-file-attachments component is used without handling the mandatory sizeExceeded event. Please add (sizeExceeded)="onAttachmentSizeExceeded($event)" to your component and implement the method to disable form submission when files exceed the size limit.');
      }
      if (!this.uploadStatusInvalid.observed) {
        console.warn('WARNING: terra-file-attachments component is used without handling the mandatory uploadStatusInvalid event. Please add (uploadStatusInvalid)="onAttachmentUploadStatusInvalid($event)" to your component and implement the method to disable form submission when files are not fully uploaded or have failed.');
      }
    });
  }

  writeValue(files: MetaFileLink[]): void {
    this.files = files || [];
    if (this.uploader) {
      this.uploader.writeValue(this.files);
    }
  }

  // Format utility methods
  truncateText(text: string, length = 35): string {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onChange = (_: any) => { };
  onTouched = () => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFilesChanged(files: MetaFileLink[]): void {
    if (files !== this.files) {
      this.files = files || [];
      this.onChange(this.files);
      this.onTouched();
      this.touchedCallback();
    }
  }

  removeFile(file: MetaFileLink): void {
    if (this.uploader) {
      this.uploader.removeFile(file);
    }
  }

  openPreview(file: MetaFileLink): void {
    this.window.open(file.url, '_blank');
  }

  open() {
    if (this.uploader && typeof this.uploader.open === 'function') {
      this.uploader.open();
    }
  }

  private calculateTotalSize(): void {
    this.totalAttachmentsSize = (this.files || []).reduce((sum, file) => sum + (file?.sizeB || 0), 0);
    this.attachmentsSizeExceeded = this.totalAttachmentsSize > this.maxTotalSize;
    this.sizeExceeded.emit(this.attachmentsSizeExceeded);
  }

  private checkUploadStatusInvalid(): void {
    // An invalid file is:
    // - progress === -1
    // - or no id (upload not finished)
    const invalid = (this.files || []).some(file =>
      file?.progress === -1 ||
      !file?.id
    );
    this.uploadStatusInvalid.emit(invalid);
  }

  get filesStatusTitle(): string {
    if (!this.files.length) return '';
    const uploadedFiles = this.files.filter((file) => file && !file.id);
    return `${this.files.length - uploadedFiles.length}/${this.files.length} files`;
  }

  get sortedFiles(): MetaFileLink[] {
    if (!this.files) return [];
    // Failed files (progress == -1) first, then the rest
    return [...this.files].sort((a, b) => {
      const aFailed = a?.progress === -1 ? 1 : 0;
      const bFailed = b?.progress === -1 ? 1 : 0;
      return bFailed - aFailed;
    });
  }
}
